import React from 'react';
import { type Theme, useStyletron } from 'baseui';
import type { StyleObject } from 'styletron-react';
import { colorWithOpacity } from '../../../../../common/utils/color-with-opacity';
import { useIsAnnotationSelected } from '../../../atoms/tool-atoms';
import { ANNOTATION_TINT_OPACITY, TIMELINE_ROW_KIND } from '../../../constants/frame-timeline-v4';
import type { TimelineActionsT, TimelineRowDescriptorT } from '../../../types/frame-timeline-v4';
import { AnnotationRowLabel } from './annotation-row-label';
import { AttributeRowLabel } from './attribute-row-label';
import { RelationshipRowLabel } from './relationship-row-label';
type PropsT = {
  descriptor: TimelineRowDescriptorT;
  timelineActions?: TimelineActionsT;
};
const getRowStyle = (theme: Theme, {
  isSelected,
  color
}: {
  isSelected: boolean;
  color?: string;
}): StyleObject => ({
  boxSizing: 'border-box',
  display: 'flex',
  alignItems: 'center',
  gap: theme.sizing.scale200,
  width: '100%',
  height: '100%',
  paddingLeft: theme.sizing.scale300,
  paddingRight: theme.sizing.scale300,
  // Selected rows are tinted with the annotation's own color; others stay transparent.
  backgroundColor: isSelected && color ? colorWithOpacity(color, ANNOTATION_TINT_OPACITY) : 'transparent'
});

// memo for the same reason as TimelineCell: the virtual grid re-renders its visible row headers on
// every scroll, so memo + stable `descriptor` props re-render only the labels that actually change.
export const RowLabel = React.memo(function RowLabel({
  descriptor,
  timelineActions
}: PropsT) {
  const [css, theme] = useStyletron();

  // Only annotation rows carry the selection highlight; attribute rows never do.
  const isSelected = useIsAnnotationSelected(descriptor.kind === TIMELINE_ROW_KIND.ANNOTATION ? descriptor.id : undefined);
  const renderContent = () => {
    switch (descriptor.kind) {
      case TIMELINE_ROW_KIND.ANNOTATION:
        return <AnnotationRowLabel descriptor={descriptor} isSelected={isSelected} onToggleExpand={timelineActions?.onToggleRowExpand} onSelect={timelineActions?.onSelectRow} />;
      case TIMELINE_ROW_KIND.ATTRIBUTE:
        return <AttributeRowLabel descriptor={descriptor} />;
      case TIMELINE_ROW_KIND.RELATIONSHIP:
        return <RelationshipRowLabel descriptor={descriptor} />;
      default:
        return null;
    }
  };
  return <div className={css(getRowStyle(theme, {
    isSelected,
    color: descriptor.color
  }))}>
      {renderContent()}
    </div>;
});